import React, { createContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

export interface ProductUsage {
  name: string;
  used: number;
  limit: number | null;
  remaining: number | null;
  can_use: boolean;
  tier_restricted: boolean;
}

export type SubscriptionTier = 'trial' | 'standard' | 'pro' | 'free';

export interface UsageSummary {
  period: string;
  subscription_status: string;
  tier?: SubscriptionTier;
  products: Record<string, ProductUsage>;
}

interface UsageContextValue {
  usageSummary: UsageSummary | null;
  loading: boolean;
  error: Error | null;
  refresh: () => void;
  getUsageForProduct: (productKey: string) => ProductUsage | null;
  currentTier: SubscriptionTier | null;
  isPro: () => boolean;
  isStandard: () => boolean;
  isTrial: () => boolean;
}

export const UsageContext = createContext<UsageContextValue | undefined>(undefined);

interface UsageProviderProps {
  children: ReactNode;
}

/**
 * Global Usage Context Provider
 *
 * Fetches and caches usage information for all products from /api/usage endpoint.
 * This prevents multiple redundant API calls when components need usage data.
 *
 * Usage summary is fetched once per page load and can be refreshed manually.
 *
 * @example
 * // In a component:
 * const { getUsageForProduct, loading, refresh } = useUsage();
 * const lessonPlanUsage = getUsageForProduct('lesson_plan');
 */
export const UsageProvider: React.FC<UsageProviderProps> = ({ children }) => {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const [usageSummary, setUsageSummary] = useState<UsageSummary | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [refreshToken, setRefreshToken] = useState<number>(0);

  const fetchUsageSummary = useCallback(async () => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const accessToken = await getAccessTokenSilently();
      const apiUrl = process.env.REACT_APP_API_SERVER_URL || '';
      const response = await fetch(`${apiUrl}/api/usage`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch usage: ${response.statusText}`);
      }

      const data: UsageSummary = await response.json();
      setUsageSummary(data);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error fetching usage');
      setError(error);
      console.error('Error fetching usage summary:', error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, getAccessTokenSilently, refreshToken]);

  useEffect(() => {
    fetchUsageSummary();
  }, [fetchUsageSummary]);

  const refresh = useCallback(() => {
    setRefreshToken(prev => prev + 1);
  }, []);

  const getUsageForProduct = useCallback((productKey: string): ProductUsage | null => {
    if (!usageSummary || !usageSummary.products) {
      return null;
    }
    return usageSummary.products[productKey] || null;
  }, [usageSummary]);

  const getCurrentTier = useCallback((): SubscriptionTier | null => {
    if (!usageSummary) {
      return null;
    }
    // Use explicit tier field if available, otherwise derive from subscription_status
    if (usageSummary.tier) {
      return usageSummary.tier;
    }
    // Fallback to subscription_status for backward compatibility
    const status = usageSummary.subscription_status.toLowerCase();
    if (status === 'free') return 'free';
    if (status === 'trialing') return 'trial';
    if (status === 'active') return 'standard'; // Default active to standard
    return 'trial'; // Default fallback
  }, [usageSummary]);

  const currentTier = getCurrentTier();

  const isPro = useCallback(() => currentTier === 'pro', [currentTier]);
  const isStandard = useCallback(() => currentTier === 'standard', [currentTier]);
  const isTrial = useCallback(() => currentTier === 'trial', [currentTier]);

  const value: UsageContextValue = {
    usageSummary,
    loading,
    error,
    refresh,
    getUsageForProduct,
    currentTier,
    isPro,
    isStandard,
    isTrial,
  };

  return (
    <UsageContext.Provider value={value}>
      {children}
    </UsageContext.Provider>
  );
};
